import React, { useState, useEffect } from "react";
import { Link, useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { BrandLogo } from "@/components/BrandLogo";
import { HumanInTheLoopCard } from "@/components/HumanInTheLoopCard";
import { toast } from "sonner";
import { Lock, Mail, ArrowRight, ShieldCheck, Sparkles, BarChart3 } from "lucide-react";
import LOGIN_BG from "@/assets/images/login-bg.png";
import RCIQ_LOGO from "@/assets/logo/rciq-dark.png";

export default function LoginPage() {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fresh = params.get("fresh") === "1";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [bannerMessage, setBannerMessage] = useState(() => {
    const m = sessionStorage.getItem("rciq_login_message");
    if (m) sessionStorage.removeItem("rciq_login_message");
    return m;
  });

  // If the link came from an email invite (`?fresh=1`) and a different user
  // is already signed in this browser, sign them out so the invitee gets a
  // clean login form instead of being bounced to the existing user's home.
  useEffect(() => {
    if (fresh && user) {
      logout();
      setBannerMessage("You've been signed out. Sign in with the credentials from your email.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fresh]);

  if (user && !fresh) {
    if (user.must_change_password) return <Navigate to="/first-login" replace />;
    // Platform admins have no org and must use the admin console.
    if (user.role === "admin" && !user.organization_id) return <Navigate to="/admin/users" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setBannerMessage(null);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      if (u?.must_change_password) { navigate("/first-login"); return; }
      if (u?.role === "admin" && !u?.organization_id) { navigate("/admin/users"); return; }
      navigate("/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Login failed";
      setBannerMessage(detail);
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-white">
      {/* Hero panel — light/teal aesthetic matching therevenuecycleiq.com */}
      <div className="relative hidden lg:block overflow-hidden bg-white">
        {/* Soft teal & sky blobs (mirror marketing "what we do" section) */}
        <div aria-hidden="true" className="absolute -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-teal-100/60 blur-3xl" />
        <div aria-hidden="true" className="absolute -bottom-32 -right-32 w-[32rem] h-[32rem] rounded-full bg-sky-100/70 blur-3xl" />
        {/* Signature gradient hairline at the top — same as marketing scroll bar */}
        <div aria-hidden="true" className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-teal-500 via-teal-400 to-sky-400" />

        <div className="relative h-full flex flex-col justify-between p-12">
          <div className="flex items-center">
            <div className="inline-block rounded-2xl bg-white shadow-[0_24px_60px_-20px_rgba(15,23,42,0.18)] ring-1 ring-slate-100 overflow-hidden p-2">
              <img
                src={RCIQ_LOGO}
                alt="Revenue Cycle IQ"
                data-testid="login-hero-logo"
                className="h-24 xl:h-28 w-auto object-contain"
              />
            </div>
          </div>

          <div className="space-y-8 max-w-lg">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-teal-700">
              <Sparkles className="h-3.5 w-3.5" />
              Denial Intelligence & Recovery Workflow Platform
            </span>
            <h1 className="font-display text-4xl xl:text-5xl font-semibold leading-[1.05] tracking-tight text-slate-900">
              The <span className="text-teal-600">Denial Intelligence & Recovery Workflow</span> Platform
            </h1>
            <p className="text-base text-slate-600 leading-relaxed">
              An intelligent denial operations platform for healthcare revenue cycle teams — across hospital, professional services, and behavioral health. Identify root causes, prioritize recoverable revenue, and orchestrate appeal workflows with payor-aware guidance. No PHI required.
            </p>
            <div className="grid grid-cols-3 gap-3 pt-2">
              {[
                { icon: BarChart3, k: "RCM",      l: "Revenue Intelligence" },
                { icon: Sparkles,  k: "AI",       l: "Insights Engine" },
                { icon: ShieldCheck, k: "PHI-Safe", l: "No PHI Required" },
              ].map((s) => (
                <div
                  key={s.l}
                  className="rounded-xl bg-white border border-slate-100 p-4 shadow-[0_2px_20px_rgba(15,23,42,0.04)] hover:shadow-[0_20px_50px_-12px_rgba(15,23,42,0.15)] hover:-translate-y-0.5 hover:border-teal-200 transition-all duration-500"
                >
                  <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center mb-2">
                    <s.icon className="h-4 w-4 text-teal-700" />
                  </div>
                  <div className="font-display text-2xl font-semibold text-slate-900">{s.k}</div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mt-0.5">{s.l}</div>
                </div>
              ))}
            </div>

            {/* Human-in-the-loop differentiator */}
            <HumanInTheLoopCard variant="card" />
          </div>

          <div className="inline-flex items-center gap-2 text-xs text-slate-500">
            <ShieldCheck className="h-3.5 w-3.5 text-teal-600" />
            De-identified data only · HIPAA-aligned posture · No PHI is stored or processed.
          </div>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-10 bg-white">
        <div className="w-full max-w-md fade-up">
          {/* Logo at top of form (mobile only — desktop has hero logo) */}
          <div className="mb-8 flex justify-center lg:hidden">
            <div className="inline-block rounded-2xl bg-white shadow-md ring-1 ring-slate-100 p-2">
              <img src={RCIQ_LOGO} alt="Revenue Cycle IQ" className="h-14 w-auto object-contain" />
            </div>
          </div>

          <div className="mb-8">
            <span className="text-xs uppercase tracking-[0.22em] font-semibold text-teal-700">Sign In</span>
            <h2 className="font-display text-3xl font-semibold text-slate-900 mt-2 tracking-tight">Sign in to Revenue Cycle IQ</h2>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              The AI denial intelligence & recovery workflow platform — for hospital, professional, and behavioral health revenue cycle teams.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5">
            {bannerMessage ? (
              <div
                role="alert"
                data-testid="login-banner"
                className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12.5px] text-red-900"
              >
                {bannerMessage}
              </div>
            ) : null}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-700" style={{ wordSpacing: "0.25em" }}>Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@organization.com"
                  className="pl-10 h-11 bg-white border-slate-200 rounded-xl focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:border-teal-500"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-700">Password</Label>
                <Link to="/forgot-password" className="text-[11px] font-semibold text-teal-700 hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter Your Password"
                  className="pl-10 h-11 bg-white border-slate-200 rounded-xl focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:border-teal-500"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={submitting}
              className="w-full h-11 bg-teal-500 hover:bg-teal-400 text-slate-900 rounded-full font-bold uppercase tracking-wider text-sm transition-colors shadow-sm"
            >
              {submitting ? "Signing In..." : (<span className="inline-flex items-center">Sign In <ArrowRight className="h-4 w-4 ml-2" /></span>)}
            </Button>

            <div className="flex items-center justify-center gap-1.5 text-xs text-slate-500">
              <ShieldCheck className="h-3.5 w-3.5 text-teal-600" />
              <span className="font-medium">PHI-Free Analytics Platform</span>
            </div>
          </form>

          <p className="text-sm text-slate-500 mt-8 text-center">
            New to RCIQ? <a href="mailto:intel@therevenuecycleiq.com?subject=Revenue%20Cycle%20IQ%20%E2%80%94%20Access%20Request" className="text-teal-700 font-semibold hover:underline" data-testid="contact-sales-link">Contact our team</a> for access.
          </p>
        </div>
      </div>
    </div>
  );
}
